/**
 * Shared pieces for "you cannot go on until these are filled in".
 *
 * The rule is the same everywhere it is used: nothing turns red until the visitor tries to
 * move on, and a field stops being red the moment they start fixing it. Marking a form red
 * before it has been attempted scolds people for not having typed yet.
 */

export type FieldErrors = Record<string, string>;

export const isBlank = (value: string | null | undefined): boolean => !value || !value.trim();

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isEmail = (value: string): boolean => EMAIL_RE.test(value.trim());

/**
 * Brings the first field that failed into view and puts the cursor in it.
 *
 * `order` is the visual order of the fields, so the visitor is always sent to the topmost
 * problem rather than to whichever key the error object happened to list first. Fields are
 * found by `data-field="<name>"`, which the form puts on the input or on its wrapper.
 */
export function focusFirstError(order: readonly string[], errors: FieldErrors): void {
  const first = order.find((name) => errors[name]);
  if (!first) return;
  const node = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
  if (!node) return;
  node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable = node.matches('input, select, textarea, button')
    ? node
    : node.querySelector<HTMLElement>('input, select, textarea, button');
  // The scroll is animated; focusing straight away would jump the page to the field instead.
  window.setTimeout(() => focusable?.focus({ preventScroll: true }), 350);
}

/** Border colours for a text input, so an errored field is visibly the odd one out. */
export function inputBorder(base: string, hasError: boolean): string {
  return hasError
    ? `${base} border-red-400 dark:border-red-500/60 bg-red-50/50 dark:bg-red-500/[0.07]`
    : `${base} border-black/[0.06] dark:border-white/[0.08]`;
}
